import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useUniverse } from "@/context/UniverseContext";
import { useSettings } from "@/context/SettingsContext";
import { SessionCard } from "@/components/SessionCard";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { GoogleReviewCard, useGoogleReviews } from "@/components/GoogleReviews";
import { ArrowRight, Dumbbell, Sparkles, Star, CalendarDays } from "lucide-react";

const FITNESS_IMG = "https://images.unsplash.com/photo-1758875568097-ba62eeff66ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const PILATES_IMG = "https://images.unsplash.com/photo-1747238415033-b74eec07eb59?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const LOGO_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_frenchies-app/artifacts/sr7wch8s_ChatGPT%20Image%2029%20avr.%202026%2C%2015_20_13.png";

const TESTIMONIALS = [
  { name: "Ana G.", text: { es: "El mejor estudio de Panamá. Los coaches son increíbles.", en: "The best studio in Panama. The coaches are incredible.", fr: "Le meilleur studio du Panama. Les coachs sont incroyables." } },
  { name: "Marco P.", text: { es: "Pasé de 0 a atleta en 4 meses. Comunidad top.", en: "Went from 0 to athlete in 4 months. Top community.", fr: "Passé de 0 à athlète en 4 mois. Communauté au top." } },
  { name: "Lucía R.", text: { es: "El Reformer cambió mi postura y mi energía.", en: "Reformer changed my posture and energy.", fr: "Le Reformer a changé ma posture et mon énergie." } },
];

export default function Home() {
  const { t, tl } = useI18n();
  const { setUniverse } = useUniverse();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const google = useGoogleReviews();
  const googleReviews = (google?.reviews || []).filter((r) => r.text).slice(0, 3);

  useEffect(() => {
    api.get("/sessions").then(({ data }) => setSessions(data.slice(0, 4))).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data || [])).catch(() => {});
  }, []);

  const goUniverse = (u) => { setUniverse(u); navigate(`/${u}`); };

  return (
    <PublicLayout>
      <Seo
        title="Frenchies Fitness & Pilates — Gym, Sport & Fitness en Coronado, Panamá"
        description="Tu Gym, Sport & Fitness en Coronado, Panamá. Clases de fitness, Pilates Reformer y coaching premium. Reserva tu clase hoy."
        path="/"
        keywords="Gym Coronado, Sport Coronado, Fitness Coronado, gimnasio Coronado, deporte Coronado, Pilates Coronado, gimnasio Coronado Panamá"
      />
      {/* HERO */}
      <section className="relative flex min-h-[92vh] w-full items-center overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 20% 40%, hsl(355 85% 55% / 0.18), transparent 70%)" }} />
        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-8 px-5 py-24 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
          <div>
            <motion.img
              src={LOGO_IMG} alt="Frenchies Fitness" data-testid="hero-logo"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="mb-7 w-full max-w-[280px] select-none drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
            />
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-gold sm:text-sm"
            >
              Fitness &amp; Pilates · Panamá
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              {tl({ es: "La energía del fitness. La elegancia del pilates.", en: "The energy of fitness. The elegance of pilates.", fr: "L'énergie du fitness. L'élégance du pilates.", ru: "Энергия фитнеса. Элегантность пилатеса." })}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 max-w-md text-base text-white/80 md:text-lg"
            >
              {t("home.heroSub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.42 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <motion.button
                data-testid="hero-book-btn" onClick={() => navigate("/schedule")}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-[0_0_34px_hsl(var(--primary)/0.5)]"
              >
                <CalendarDays className="h-5 w-5" />
                {tl({ es: "Reservar mi clase", en: "Book my session", fr: "Réserver ma séance", ru: "Записаться на занятие" })}
                <ArrowRight className="h-4 w-4" />
              </motion.button>
              <Button size="lg" variant="outline" data-testid="hero-pricing-btn" onClick={() => navigate("/pricing")}
                className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20">
                {t("nav.pricing")}
              </Button>
            </motion.div>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* TWO UNIVERSES */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <h2 className="mb-10 font-display text-3xl font-bold tracking-tight md:text-4xl">{t("home.twoUniverses")}</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {[
            { u: "fitness", img: FITNESS_IMG, icon: Dumbbell, color: "hsl(355 85% 55%)", label: "Frenchies Fitness", desc: { es: "Athletic Fitness, Force, Calisthenics y más.", en: "Athletic Fitness, Strength, Calisthenics and more.", fr: "Athletic Fitness, Force, Calisthenics et plus." } },
            { u: "pilates", img: PILATES_IMG, icon: Sparkles, color: "hsl(217 91% 62%)", label: "Frenchies Pilates", desc: { es: "Reformer, cursos privados y semi-privados.", en: "Reformer, private and semi-private classes.", fr: "Reformer, cours privés et semi-privés." } },
          ].map(({ u, img, icon: Icon, color, label, desc }) => (
            <motion.button
              key={u}
              data-testid={`universe-card-${u}`}
              onClick={() => goUniverse(u)}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              whileHover={{ y: -6 }}
              className="group relative h-80 overflow-hidden rounded-2xl text-left md:h-[420px]"
            >
              <img src={img} alt={label} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="hero-overlay absolute inset-0" />
              <div className="relative z-10 flex h-full flex-col justify-end p-7">
                <Icon className="mb-3 h-8 w-8 text-white" style={{ color }} />
                <h3 className="font-display text-2xl font-bold text-white">{label}</h3>
                <p className="mt-1 max-w-sm text-sm text-white/85">{tl(desc)}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
                  {u === "fitness" ? t("home.exploreFitness") : t("home.explorePilates")} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* UPCOMING CLASSES */}
      {sessions.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8">
          <div className="mb-8 flex items-end justify-between">
            <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t("home.upcomingClasses")}</h2>
            <Button variant="ghost" data-testid="see-schedule-btn" onClick={() => navigate("/schedule")} className="rounded-full">
              {t("nav.schedule")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {sessions.map((s) => <SessionCard key={s.session_id} session={s} compact onBook={() => navigate("/schedule")} onWaitlist={() => navigate("/schedule")} />)}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t("home.testimonials")}</h2>
              {settings?.review_rating && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-primary text-primary" />)}</span>
                  <span className="font-semibold">{settings.review_rating}</span>
                  {settings?.review_count ? <span className="text-muted-foreground">· {settings.review_count} {tl({ es: "reseñas en Google", en: "Google reviews", fr: "avis Google" })}</span> : null}
                </div>
              )}
            </div>
            {settings?.google_review_url && (
              <a href={settings.google_review_url} target="_blank" rel="noopener noreferrer" data-testid="home-google-reviews-btn"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold transition-transform hover:scale-105">
                <Star className="h-4 w-4 fill-primary text-primary" /> {tl({ es: "Ver todas en Google", en: "See all on Google", fr: "Voir tous nos avis Google" })}
              </a>
            )}
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {googleReviews.length
              ? googleReviews.map((r, i) => <GoogleReviewCard key={r.url || `${r.author}-${r.relative_time}`} r={r} index={i} />)
              : (reviews.length ? reviews : TESTIMONIALS).map((tst, i) => (
              <div key={tst.review_id || i} data-testid={`review-card-${i}`} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-3 flex gap-0.5">{[...Array(tst.rating || 5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-primary text-primary" />)}</div>
                <p className="text-sm leading-relaxed">{typeof tst.text === "string" ? tst.text : tl(tst.text)}</p>
                <div className="mt-4 font-display font-semibold">{tst.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL SEO */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8" data-testid="local-seo">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          {tl({ es: "Tu gym, functional movement y fitness en Coronado, Panamá",
                en: "Your gym, functional movement & fitness in Coronado, Panama",
                fr: "Ton gym, functional movement et fitness à Coronado, Panama" })}
        </h2>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          {tl({
            es: "Frenchies Fitness & Pilates es el gym de referencia en Coronado para el fitness, el functional movement y el deporte. En pleno Coronado, Panamá Oeste, ofrecemos clases de functional movement (movimiento funcional), fitness funcional, calistenia, fuerza y Pilates Reformer, con coaching premium y clases privadas. Si buscas un gym en Coronado, functional movement en Coronado o clases de fitness en Coronado, entrena con nosotros y reserva tu clase hoy.",
            en: "Frenchies Fitness & Pilates is the go-to gym in Coronado for fitness, functional movement and sport. Right in Coronado, Panamá Oeste, we offer functional movement, functional fitness, calisthenics, strength and Pilates Reformer, with premium coaching and private classes. Looking for a gym in Coronado, functional movement in Coronado or fitness classes in Coronado? Train with us and book your class today.",
            fr: "Frenchies Fitness & Pilates est le gym de référence à Coronado pour le fitness, le functional movement et le sport. En plein cœur de Coronado (Panamá Oeste), nous proposons functional movement, fitness fonctionnel, calisthénie, force et Pilates Reformer, avec coaching premium et cours privés. Tu cherches un gym à Coronado, du functional movement à Coronado ou des cours de fitness à Coronado ? Entraîne-toi avec nous et réserve ta séance dès aujourd'hui." })}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button variant="outline" data-testid="home-gym-coronado-link" onClick={() => navigate("/gym-coronado")} className="rounded-full">
            {tl({ es: "Descubre nuestro gym en Coronado", en: "Discover our gym in Coronado", fr: "Découvre notre gym à Coronado" })} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="outline" data-testid="home-pilates-coronado-link" onClick={() => navigate("/pilates-coronado")} className="rounded-full">
            {tl({ es: "Pilates en Coronado", en: "Pilates in Coronado", fr: "Pilates à Coronado" })} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="outline" data-testid="home-fm-coronado-link" onClick={() => navigate("/functional-movement-coronado")} className="rounded-full">
            {tl({ es: "Functional Movement en Coronado", en: "Functional Movement in Coronado", fr: "Functional Movement à Coronado" })} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="outline" data-testid="home-horarios-link" onClick={() => navigate("/horarios")} className="rounded-full">
            {tl({ es: "Horarios y cómo llegar", en: "Hours & directions", fr: "Horaires et itinéraire" })} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="ghost" data-testid="home-blog-link" onClick={() => navigate("/blog")} className="rounded-full">
            {tl({ es: "Leer el blog", en: "Read the blog", fr: "Lire le blog" })} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-5 py-20 text-center lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t("home.ctaTitle")}</h2>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">{t("home.ctaSub")}</p>
        <Button size="lg" data-testid="cta-trial-btn" onClick={() => navigate("/register")} className="mt-7 rounded-full">
          {t("home.tryTrial")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </section>
    </PublicLayout>
  );
}
